// أنواع العمليات المختلفة في النظام
export enum ActivityType {
  // عمليات الصيانة
  MAINTENANCE_CREATE = 'maintenance_create',
  MAINTENANCE_UPDATE = 'maintenance_update',
  MAINTENANCE_DELETE = 'maintenance_delete',
  MAINTENANCE_STATUS_CHANGE = 'maintenance_status_change',
  MAINTENANCE_PAYMENT_ADD = 'maintenance_payment_add',
  MAINTENANCE_PARTS_ADD = 'maintenance_parts_add',
  MAINTENANCE_PARTS_REMOVE = 'maintenance_parts_remove',
  
  // عمليات العملاء
  CUSTOMER_CREATE = 'customer_create',
  CUSTOMER_UPDATE = 'customer_update',
  CUSTOMER_DELETE = 'customer_delete',
  
  // عمليات الأجهزة
  DEVICE_CREATE = 'device_create',
  DEVICE_UPDATE = 'device_update',
  DEVICE_DELETE = 'device_delete',
  
  // عمليات المخزون
  INVENTORY_CREATE = 'inventory_create',
  INVENTORY_UPDATE = 'inventory_update',
  INVENTORY_DELETE = 'inventory_delete',
  INVENTORY_STOCK_CHANGE = 'inventory_stock_change',
  
  // عمليات المبيعات
  SALE_CREATE = 'sale_create',
  SALE_UPDATE = 'sale_update',
  SALE_DELETE = 'sale_delete',
  SALE_RETURN = 'sale_return',
  
  // عمليات المستخدمين
  USER_CREATE = 'user_create',
  USER_UPDATE = 'user_update',
  USER_DELETE = 'user_delete',
  USER_LOGIN = 'user_login',
  USER_LOGOUT = 'user_logout',
  USER_PERMISSION_CHANGE = 'user_permission_change',
  
  // عمليات الصور
  IMAGE_UPLOAD = 'image_upload',
  IMAGE_DELETE = 'image_delete',
  
  // عمليات الإعدادات
  SETTINGS_UPDATE = 'settings_update',
  
  // عمليات أخرى
  BACKUP_CREATE = 'backup_create',
  BACKUP_RESTORE = 'backup_restore',
  REPORT_GENERATE = 'report_generate',
  PRINT_DOCUMENT = 'print_document'
}

// مستوى أهمية العملية
export enum ActivityLevel {
  LOW = 'low',        // عمليات عادية
  MEDIUM = 'medium',  // عمليات مهمة
  HIGH = 'high',      // عمليات حساسة
  CRITICAL = 'critical' // عمليات حرجة
}

// حالة العملية
export enum ActivityStatus {
  SUCCESS = 'success',
  FAILED = 'failed',
  PENDING = 'pending'
}

// نموذج سجل النشاط
export interface ActivityLog {
  id: string;
  
  // معلومات العملية
  type: ActivityType;
  level: ActivityLevel;
  status: ActivityStatus;
  
  // معلومات المستخدم
  userId: string;
  userName: string;
  userRole: string;
  userBranch?: string;
  
  // تفاصيل العملية
  title: string;           // عنوان مختصر للعملية
  description: string;     // وصف تفصيلي
  
  // البيانات المتأثرة
  targetType: string;      // نوع الكائن المتأثر (maintenance, customer, etc.)
  targetId: string;        // معرف الكائن المتأثر
  targetName?: string;     // اسم الكائن المتأثر
  
  // البيانات قبل وبعد التغيير
  oldData?: any;          // البيانات قبل التغيير
  newData?: any;          // البيانات بعد التغيير
  changes?: string[];     // قائمة بالحقول المتغيرة
  
  // معلومات إضافية
  ipAddress?: string;     // عنوان IP
  userAgent?: string;     // معلومات المتصفح
  sessionId?: string;     // معرف الجلسة
  
  // معلومات التوقيت
  createdAt: Date | string;
  
  // معلومات إضافية
  metadata?: {
    [key: string]: any;
  };
}

// فلتر البحث في سجل النشاط
export interface ActivityLogFilter {
  // فلتر حسب المستخدم
  userId?: string;
  userRole?: string;
  userBranch?: string;
  
  // فلتر حسب نوع العملية
  type?: ActivityType;
  types?: ActivityType[];
  level?: ActivityLevel;
  status?: ActivityStatus;
  
  // فلتر حسب الكائن المتأثر
  targetType?: string;
  targetId?: string;
  
  // فلتر حسب التاريخ
  startDate?: Date | string;
  endDate?: Date | string;
  
  // فلتر حسب النص
  searchText?: string;
  
  // ترتيب وترقيم
  sortBy?: 'createdAt' | 'type' | 'level' | 'userName';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

// إحصائيات سجل النشاط
export interface ActivityLogStats {
  totalActivities: number;
  activitiesByType: Record<ActivityType, number>;
  activitiesByLevel: Record<ActivityLevel, number>;
  activitiesByStatus: Record<ActivityStatus, number>;
  activitiesByUser: Record<string, number>;
  activitiesByDate: Record<string, number>;
  mostActiveUsers: Array<{
    userId: string;
    userName: string;
    count: number;
  }>;
  recentCriticalActivities: ActivityLog[];
}

// نتيجة البحث في سجل النشاط
export interface ActivityLogSearchResult {
  activities: ActivityLog[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// إعدادات سجل النشاط
export interface ActivityLogSettings {
  enabled: boolean;
  retentionDays: number;        // عدد الأيام للاحتفاظ بالسجلات
  logLevels: ActivityLevel[];   // المستويات المراد تسجيلها
  logTypes: ActivityType[];     // أنواع العمليات المراد تسجيلها
  includeOldData: boolean;      // تسجيل البيانات القديمة
  includeNewData: boolean;      // تسجيل البيانات الجديدة
  includeUserAgent: boolean;    // تسجيل معلومات المتصفح
  includeIpAddress: boolean;    // تسجيل عنوان IP
}

// ترجمة أنواع العمليات للعربية
export const ActivityTypeLabels: Record<ActivityType, string> = {
  // عمليات الصيانة
  [ActivityType.MAINTENANCE_CREATE]: 'إنشاء طلب صيانة',
  [ActivityType.MAINTENANCE_UPDATE]: 'تحديث طلب صيانة',
  [ActivityType.MAINTENANCE_DELETE]: 'حذف طلب صيانة',
  [ActivityType.MAINTENANCE_STATUS_CHANGE]: 'تغيير حالة الصيانة',
  [ActivityType.MAINTENANCE_PAYMENT_ADD]: 'إضافة دفعة للصيانة',
  [ActivityType.MAINTENANCE_PARTS_ADD]: 'إضافة قطع غيار',
  [ActivityType.MAINTENANCE_PARTS_REMOVE]: 'إزالة قطع غيار',
  
  // عمليات العملاء
  [ActivityType.CUSTOMER_CREATE]: 'إنشاء عميل جديد',
  [ActivityType.CUSTOMER_UPDATE]: 'تحديث بيانات عميل',
  [ActivityType.CUSTOMER_DELETE]: 'حذف عميل',
  
  // عمليات الأجهزة
  [ActivityType.DEVICE_CREATE]: 'إنشاء جهاز جديد',
  [ActivityType.DEVICE_UPDATE]: 'تحديث بيانات جهاز',
  [ActivityType.DEVICE_DELETE]: 'حذف جهاز',
  
  // عمليات المخزون
  [ActivityType.INVENTORY_CREATE]: 'إضافة منتج للمخزون',
  [ActivityType.INVENTORY_UPDATE]: 'تحديث منتج في المخزون',
  [ActivityType.INVENTORY_DELETE]: 'حذف منتج من المخزون',
  [ActivityType.INVENTORY_STOCK_CHANGE]: 'تغيير كمية المخزون',
  
  // عمليات المبيعات
  [ActivityType.SALE_CREATE]: 'إنشاء عملية بيع',
  [ActivityType.SALE_UPDATE]: 'تحديث عملية بيع',
  [ActivityType.SALE_DELETE]: 'حذف عملية بيع',
  [ActivityType.SALE_RETURN]: 'إرجاع منتجات',
  
  // عمليات المستخدمين
  [ActivityType.USER_CREATE]: 'إنشاء مستخدم جديد',
  [ActivityType.USER_UPDATE]: 'تحديث بيانات مستخدم',
  [ActivityType.USER_DELETE]: 'حذف مستخدم',
  [ActivityType.USER_LOGIN]: 'تسجيل دخول',
  [ActivityType.USER_LOGOUT]: 'تسجيل خروج',
  [ActivityType.USER_PERMISSION_CHANGE]: 'تغيير صلاحيات مستخدم',
  
  // عمليات الصور
  [ActivityType.IMAGE_UPLOAD]: 'رفع صورة',
  [ActivityType.IMAGE_DELETE]: 'حذف صورة',
  
  // عمليات الإعدادات
  [ActivityType.SETTINGS_UPDATE]: 'تحديث الإعدادات',
  
  // عمليات أخرى
  [ActivityType.BACKUP_CREATE]: 'إنشاء نسخة احتياطية',
  [ActivityType.BACKUP_RESTORE]: 'استعادة نسخة احتياطية',
  [ActivityType.REPORT_GENERATE]: 'إنشاء تقرير',
  [ActivityType.PRINT_DOCUMENT]: 'طباعة مستند'
};

// ترجمة مستويات الأهمية للعربية
export const ActivityLevelLabels: Record<ActivityLevel, string> = {
  [ActivityLevel.LOW]: 'منخفض',
  [ActivityLevel.MEDIUM]: 'متوسط',
  [ActivityLevel.HIGH]: 'عالي',
  [ActivityLevel.CRITICAL]: 'حرج'
};

// ترجمة حالات العملية للعربية
export const ActivityStatusLabels: Record<ActivityStatus, string> = {
  [ActivityStatus.SUCCESS]: 'نجح',
  [ActivityStatus.FAILED]: 'فشل',
  [ActivityStatus.PENDING]: 'قيد التنفيذ'
};

// ألوان مستويات الأهمية
export const ActivityLevelColors: Record<ActivityLevel, string> = {
  [ActivityLevel.LOW]: 'default',
  [ActivityLevel.MEDIUM]: 'info',
  [ActivityLevel.HIGH]: 'warning',
  [ActivityLevel.CRITICAL]: 'error'
};

// ألوان حالات العملية
export const ActivityStatusColors: Record<ActivityStatus, string> = {
  [ActivityStatus.SUCCESS]: 'success',
  [ActivityStatus.FAILED]: 'error',
  [ActivityStatus.PENDING]: 'warning'
};
